import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import { Box, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { GetCommercialMonitoringDetailReceptionBookAPI } from "../api";
import { useLegalAffairsBureauName } from "../hooks/useLegalAffairsBureauName";
import {
  type CommercialMonitoringSummaryRow,
  type CommercialMonitoringDetailRow,
  type CommercialMonitoringDetailReceptionBookRow,
  type GetCommercialMonitoringReceptionBooksAPIResponse,
} from "../types";
import { PagePaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { CommercialMonitoringDetailReceptionBookDataGrid } from "../components/CommercialMonitoringDetailReceptionBookDataGrid";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { HttpStatusCode, isAxiosError } from "axios";
import { getDisplayReceptionReason } from "@/features/commercialRegistrationMonitoring/utils/getDisplayReceptionReason";
import { getReceptionBookReasonNames } from "@/features/commercialRegistrationMonitoring/utils/getReceptionBookReasonNames";

/**
 * 商業登記モニタリング詳細受付帳結果画面
 */
export const CommercialMonitoringDetailReceptionBook: React.FC = () => {
  const { id } = useParams();
  const { apiClient } = useApiClient();
  const resolveLegalAffairsBureauName = useLegalAffairsBureauName();

  const { data, isLoading, error } =
    GetCommercialMonitoringDetailReceptionBookAPI(apiClient, Number(id));

  const monitoringSummary = useMemo<CommercialMonitoringSummaryRow | null>(
    () =>
      data
        ? {
            id: data.summary.id,
            count: data.summary.count,
            name: data.summary.name,
            receptionReasons: getDisplayReceptionReason(
              data.summary.receptionReasons
            ),
            monthlyUpdatable: data.summary.monthlyUpdatable ? "する" : "しない",
            legalAffairsBureauRequestDateStart:
              data.summary.legalAffairsBureauRequestDateStart ?? undefined,
            legalAffairsBureauRequestDateEnd:
              data.summary.legalAffairsBureauRequestDateEnd ?? undefined,
            createdAt: data.summary.createdAt,
          }
        : null,
    [data]
  );

  const monitoringDetail = useMemo<CommercialMonitoringDetailRow | null>(
    () =>
      data
        ? {
            id: data.detail.id,
            commercialRegistrationNumber:
              data.detail.requesterCommercialRegistrationNumber,
            corporateName: data.detail.corporateName,
            legalAffairsBureauName: resolveLegalAffairsBureauName(
              data.detail.legalAffairsBureauId
            ),
            registrationDate: data.detail.registrationDate,
            remarks: data.detail.remarks,
          }
        : null,
    [data, resolveLegalAffairsBureauName]
  );

  // 受付帳結果
  const rows = useMemo<CommercialMonitoringDetailReceptionBookRow[]>(
    () =>
      data?.receptionBooks.map((receptionBook) => ({
        id: receptionBook.id,
        legalAffairsBureauRequestDate:
          receptionBook.legalAffairsBureauRequestDate,
        receptionReason: getReceptionBookReasonNames(
          receptionBook.commercialReceptionReasons
        ),
      })) ?? [],
    [data]
  );

  useEffect(() => {
    if (
      isAxiosError<GetCommercialMonitoringReceptionBooksAPIResponse>(error) &&
      (error.response?.status === HttpStatusCode.BadRequest ||
        error.response?.status === HttpStatusCode.NotFound)
    ) {
      toast.error("指定のデータが存在しません。");
      return;
    }
    if (
      isAxiosError<GetCommercialMonitoringReceptionBooksAPIResponse>(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      toast.error(
        "データの取得に失敗しました。しばらく経ってから再度お試しください。"
      );
    }
  }, [error]);

  return (
    <PagePaper>
      {/* ページタイトル */}
      <Box sx={{ display: "flex", mb: 3 }}>
        <MonitorHeartIcon sx={{ mr: 1 }} fontSize="large" />
        <PageTitle>商業登記モニタリング詳細受付帳結果</PageTitle>
      </Box>

      {/* モニタリング詳細の情報 */}
      <Stack direction={"column"} spacing={1} sx={{ mt: 3 }}>
        <Stack direction={"row"} spacing={1}>
          <Typography>会社法人等番号：</Typography>
          <Typography>
            {monitoringDetail?.commercialRegistrationNumber}
          </Typography>
        </Stack>
        <Stack direction={"row"} spacing={1}>
          <Typography>法人名：</Typography>
          <Typography>{monitoringDetail?.corporateName}</Typography>
        </Stack>
        <Stack direction={"row"} spacing={1}>
          <Typography>法務局：</Typography>
          <Typography>{monitoringDetail?.legalAffairsBureauName}</Typography>
        </Stack>
      </Stack>

      {/* 結果 */}
      <Box sx={{ mt: 3 }}>
        {isLoading || data == null ? (
          <Stack alignItems="center">
            <CircularProgress sx={{ my: "20px" }} />
          </Stack>
        ) : (
          <CommercialMonitoringDetailReceptionBookDataGrid rows={rows} />
        )}
      </Box>

      {/* 戻るリンク */}
      <Box sx={{ mt: 3 }}>
        <Link
          component={RouterLink}
          to={
            monitoringSummary != null
              ? `/commercial-monitoring/${monitoringSummary.id}`
              : "/commercial-monitoring"
          }
          sx={{ display: "inline-block" }}
        >
          <Box sx={{ display: "flex" }}>
            <KeyboardArrowLeft />
            <Typography>
              {monitoringSummary != null
                ? "商業登記モニタリング詳細に戻る"
                : "商業登記モニタリング一覧に戻る"}
            </Typography>
          </Box>
        </Link>
      </Box>
    </PagePaper>
  );
};
